import { StorageSerializers, useLocalStorage } from '@vueuse/core'
import { createSharedComposable } from '@vueuse/shared'
import { useMyFetch } from '~/features/shared/useMyFetch'
import type { City } from '~/model/interfaces/City'
import type { ProductDictionary } from '~/model/interfaces/ProductDictionary'
import type { DeliveryType } from '~/model/interfaces/DeliveryType'
import type { OrderDictionary } from '~/model/interfaces/OrderDictionary'
import type { Warehouse } from '~/model/interfaces/Warehouse/Warehouse'
import type { CreatedUser } from '~/model/interfaces/CreatedUser'
import type { WarehouseDictionary } from '~/model/interfaces/WarehouseDictionary'
import { useSelectInputAdapter } from '~/features/useSelectInputAdapter'
import type { FurnitureAssemblyDictionary } from '~/model/interfaces/FurnitureAssemblyDictionary'
import type { Direction } from '~/model/interfaces/Directions/types'

export const useGlobalData = createSharedComposable(() => {
    const updateInterval = 7 * 24 * 60 * 60 * 1000

    const route = useRoute()
    const { $api } = useNuxtApp()
    const lastUpdate = useLocalStorage('vm-last-update', Date.now())
    const isNeedToUpdate = unref(lastUpdate) + updateInterval < Date.now() || route.query.forceUpdate

    const cities = useLocalStorage<City[]>('vm-cities', [], { serializer: StorageSerializers.object })
    const directions = useLocalStorage<Record<string, Direction[]>>('vm-directions', {}, { serializer: StorageSerializers.object })

    const productDictionary = useLocalStorage<ProductDictionary | null>('vm-product-dictionaries', null, { serializer: StorageSerializers.object })
    const mattressTypesForSelect = useSelectInputAdapter(productDictionary.value?.mattressType, {
        value: 'id',
        label: 'name',
    })
    const productBedSizesForSelect = useSelectInputAdapter(productDictionary.value?.bedSize, {
        value: 'id',
        label: 'size',
    })
    const kitchenSizesForSelect = useSelectInputAdapter(productDictionary.value?.kitchenSize, {
        value: 'id',
        label: 'size',
    })
    const colorsForSelect = computed(() => productDictionary.value?.simpleColor?.map((color) => {
        const typeName = productDictionary.value?.colorType?.find(type => type.id === color.typeId)?.name ?? ''
        return {
            value: color.id,
            label: typeName ? `${color.name} (${typeName})` : color.name,
            hex: color.hex,
        }
    }) ?? [])

    const colorTypesForSelect = computed(() => productDictionary.value?.colorType?.map(type => ({
        value: type.id,
        label: type.name,
    })) ?? [])

    function getColorById(id: unknown) {
        return colorsForSelect.value.find(color => color.value === Number(id))
    }

    const orderDictionary = useLocalStorage<OrderDictionary | null>('vm-order-dictionaries', null, { serializer: StorageSerializers.object })
    const warehouseDictionary = useLocalStorage<WarehouseDictionary | null>('vm-warehouse-dictionaries', null, { serializer: StorageSerializers.object })
    const furnitureAssemblyDictionaries = useLocalStorage<FurnitureAssemblyDictionary | null>('vm-furniture-collecting-dictionaries', null, { serializer: StorageSerializers.object })
    const deliveryTypes = useLocalStorage<DeliveryType[]>('vm-delivery-types', [], { serializer: StorageSerializers.object })
    const roles = useLocalStorage<{
        id: number,
        name: string
    }[]>('vm-roles', [], { serializer: StorageSerializers.object })
    const warehouses = useLocalStorage<Warehouse[]>('vm-warehouses', [], { serializer: StorageSerializers.object })
    const users = ref<CreatedUser[]>([])
    const warehousePositionSuppliersById = useLocalStorage<Record<number, number[]>>('vm-warehouse-positions-by-supplier', [], { serializer: StorageSerializers.object })

    const citiesForSelect = useSelectInputAdapter(cities, {
        value: 'id',
        label: 'name',
    })

    const productTypes = computed(() => unref(productDictionary)?.type ?? [])
    const productTypesForSelect = useSelectInputAdapter(productTypes, {
        value: 'id',
        label: 'name',
    })
    const productPriceTypes = computed(() => unref(productDictionary)?.priceType ?? [])
    const productPriceTypesForSelect = useSelectInputAdapter(productPriceTypes, {
        value: 'id',
        label: 'name',
    })
    const productBedBaseTypes = computed(() => unref(productDictionary)?.bedBaseType ?? [])

    const productColorTypes = computed(() => unref(productDictionary)?.colorType ?? [])
    const productDetailTypes = computed(() => unref(productDictionary)?.details ?? [])
    const productDetailTypesForSelect = useSelectInputAdapter(productDetailTypes, {
        value: 'id',
        label: 'name',
    })
    const suppliers = computed(() => unref(productDictionary)?.suppliers ?? [])
    const deliveryTypesForSelect = useSelectInputAdapter(deliveryTypes, {
        value: 'id',
        label: 'name',
    })
    const suppliersForSelect = useSelectInputAdapter(productDictionary.value?.suppliers, {
        value: 'id',
        label: 'name',
    })
    const orderStatuses = computed(() => unref(orderDictionary)?.statuses ?? [])
    const orderStatusesForSelect = useSelectInputAdapter(orderStatuses, {
        value: 'id',
        label: 'name',
    })
    const sourceById = computed(() => {
        const sources = unref(orderDictionary)?.sources ?? []

        return Object.fromEntries(sources.map(source => [source.id, source]))
    })

    async function syncCities() {
        if (!unref(cities).length || isNeedToUpdate) {
            const { data, status } = await useMyFetch<City[]>('city/all')

            if (unref(status) === 'success') {
                lastUpdate.value = Date.now()
                cities.value = unref(data)
            }
        }
    }

    async function syncDirections() {
        if (!Object.keys(unref(directions)).length || isNeedToUpdate) {
            const { data, status } = await useMyFetch<Record<string, Direction[]>>('city/directionsByMainCityId ')

            if (unref(status) === 'success') {
                lastUpdate.value = Date.now()
                directions.value = unref(data)
            }
        }
    }

    async function syncProductDictionaries(forceUpdate = false) {
        if (forceUpdate || !unref(productDictionary) || isNeedToUpdate) {
            try {
                const data = await $api<ProductDictionary>('product/dictionaries')
                lastUpdate.value = Date.now()
                productDictionary.value = data
            } catch (error) {
                console.error(error)
            }
        }
    }

    async function syncWarehouseDictionaries() {
        if (!unref(warehouseDictionary) || isNeedToUpdate) {
            const { data, status } = await useMyFetch<WarehouseDictionary>('warehouse/dictionaries')

            if (unref(status) === 'success') {
                lastUpdate.value = Date.now()
                warehouseDictionary.value = unref(data)
            }
        }
    }

    async function syncFurnitureAssemblyDictionaries() {
        if (!unref(furnitureAssemblyDictionaries) || isNeedToUpdate) {
            const { data, status } = await useMyFetch<WarehouseDictionary>('order/furnitureAssembly/dictionaries')

            if (unref(status) === 'success') {
                lastUpdate.value = Date.now()
                furnitureAssemblyDictionaries.value = unref(data)
            }
        }
    }

    async function syncOrderDictionaries() {
        if (!unref(orderDictionary) || isNeedToUpdate) {
            const { data, status } = await useMyFetch<OrderDictionary>('order/dictionaries')
            if (unref(status) === 'success') {
                lastUpdate.value = Date.now()
                orderDictionary.value = unref(data)
            }
        }
    }

    async function syncDeliveryTypes() {
        if (!unref(deliveryTypes).length || isNeedToUpdate) {
            const { data, status } = await useMyFetch<City[]>('delivery/types/all')
            if (unref(status) === 'success') {
                lastUpdate.value = Date.now()
                deliveryTypes.value = unref(data)
            }
        }
    }

    async function syncRoles() {
        if (!unref(roles).length || isNeedToUpdate) {
            const { data, status } = await useMyFetch<{ id: number, name: string }[]>('role/all')
            if (unref(status) === 'success') {
                lastUpdate.value = Date.now()
                roles.value = unref(data)
            }
        }
    }

    async function syncWarehouses(forceUpdate = false) {
        if (!unref(warehouses).length || isNeedToUpdate || forceUpdate) {
            const { data, status } = await useMyFetch<Warehouse[]>('warehouse/all')

            if (unref(status) === 'success') {
                lastUpdate.value = Date.now()
                warehouses.value = unref(data)
            }
        }
    }

    async function syncUsers(forceUpdate = false) {
        if (!unref(users).length || isNeedToUpdate || forceUpdate) {
            const { data, status } = await useMyFetch<CreatedUser[]>('user/all')

            if (unref(status) === 'success') {
                lastUpdate.value = Date.now()
                users.value = unref(data)
            }
        }
    }

    async function syncWarehousePositionSuppliersById(forceUpdate = false) {
        if (!Object.keys(unref(warehousePositionSuppliersById))?.length || isNeedToUpdate || forceUpdate) {
            const {
                data,
                status,
            } = await useMyFetch<Record<number, number[]>>('warehouse/warehousePositionSuppliersById')

            if (unref(status) === 'success') {
                lastUpdate.value = Date.now()
                warehousePositionSuppliersById.value = unref(data)
            }
        }
    }

    async function syncGlobalData() {
        await Promise.all([
            syncCities(),
            syncDirections(),
            syncProductDictionaries(true),
            syncOrderDictionaries(),
            syncWarehouseDictionaries(),
            syncFurnitureAssemblyDictionaries(),
            syncDeliveryTypes(),
            syncRoles(),
            syncWarehouses(),
            syncUsers(),
            syncWarehousePositionSuppliersById(),
        ])

        if (route.query.forceUpdate) {
            // await router.replace({ query: {} })
        }
    }

    function getCityNameById(id: number) {
        return unref(cities).find(city => city.id === id)?.name ?? ''
    }

    function getDeliveryTypeById(id: number) {
        return unref(deliveryTypes).find(type => type.id === id)?.name ?? ''
    }

    function getSupplierNameById(id: number) {
        return unref(suppliers).find(supplier => supplier.id === id)?.name ?? ''
    }

    function getSourceById(id: number) {
        return unref(sourceById)[id]
    }

    return {
        getSourceById,
        getSupplierNameById,
        getDeliveryTypeById,
        getCityNameById,
        syncWarehouses,
        syncGlobalData,
        syncProductDictionaries,
        syncUsers,
        citiesForSelect,
        users,
        warehouses,
        cities,
        directions,
        productDictionary,
        kitchenSizesForSelect,
        productBedSizesForSelect,
        mattressTypesForSelect,
        colorsForSelect,
        colorTypesForSelect,
        getColorById,
        warehouseDictionary,
        productBedBaseTypes,
        orderDictionary,
        productTypes,
        productTypesForSelect,
        productPriceTypes,
        productPriceTypesForSelect,
        productColorTypes,
        productDetailTypes,
        productDetailTypesForSelect,
        deliveryTypes,
        deliveryTypesForSelect,
        suppliersForSelect,
        suppliers,
        orderStatuses,
        orderStatusesForSelect,
        sourceById,
        warehousePositionSuppliersById,
        furnitureAssemblyDictionaries,
    }
})
